import { type ReactNode, useId, useState } from "react";
import { useInView } from "react-intersection-observer";

type LazyHydrateProps = {
  children: ReactNode;
  /*
   * How early to hydrate, as an IntersectionObserver `rootMargin`. The
   * default starts the work roughly one phone screen before the block is
   * reached, so it is interactive by the time it is actually on screen.
   */
  rootMargin?: string;
  /* Layout classes for the wrapper element this adds around `children`. */
  className?: string;
};

/*
 * Decides, on the client's hydration pass, whether to skip hydrating this
 * block. It reads the server markup directly, which is already laid out
 * because the stylesheet is render-blocking.
 */
const shouldDefer = (id: string, rootMargin: string) => {
  if (typeof window === "undefined") {
    return false;
  }

  const element = document.querySelector(`[data-lazy-hydrate="${id}"]`);

  // No server markup to keep: a client-side navigation or a remount.
  if (!element) {
    return false;
  }

  // Not rendered at all, e.g. the desktop mega-menu on a phone.
  if (element.getClientRects().length === 0) {
    return true;
  }

  // ponytail: reads the vertical margin in px only, which is all we pass.
  const margin = parseInt(rootMargin, 10) || 0;
  const { top, bottom } = element.getBoundingClientRect();

  return top > window.innerHeight + margin || bottom < -margin;
};

/**
 * Server-renders `children` normally, then leaves that markup alone on the
 * client until the block comes near the viewport.
 *
 * React hydrating the whole page at once is the single biggest chunk of
 * first-party main-thread work on mobile. Most of it pays for markup a
 * phone user cannot see yet: blocks below the fold, and the desktop
 * mega-menu, which is `display: none` below `md` but was hydrated anyway.
 *
 * The SSR HTML is byte-for-byte unchanged apart from a `data-lazy-hydrate`
 * attribute, so crawlers, plain links and `Ctrl+F` see what they saw before.
 *
 * How it works: on the hydration pass, a block that is hidden or away from
 * the viewport returns an empty element carrying `dangerouslySetInnerHTML`,
 * which tells React the subtree is not React's to reconcile. React leaves
 * the server HTML in the DOM and skips hydrating it. Once `useInView`
 * reports the block is close, the real children render and replace that
 * markup, so the block is built from scratch rather than hydrated.
 *
 * A block that is already near the viewport when the page hydrates is
 * hydrated normally, keeping its DOM. That matters for anything that paints
 * early: rebuilding an image that already painted would make it a new LCP
 * candidate. Client-side mounts also render normally, since there is no
 * server markup to keep.
 *
 * A deferred block is inert until it hydrates, so it suits sections whose
 * content is links, images and copy.
 *
 * A block that can render nothing needs `className="empty:hidden"`: the
 * wrapper is always in the DOM, and an empty one would otherwise pick up a
 * parent's `space-y-*` gap.
 *
 * `_app.js` calls `defaultFallbackInView(true)`, so a browser without
 * IntersectionObserver reports "in view" from the start.
 */
export const LazyHydrate = ({
  children,
  rootMargin = "200px 0px",
  className,
}: LazyHydrateProps) => {
  const id = useId();
  const [isDeferred] = useState(() => shouldDefer(id, rootMargin));

  const { ref, inView } = useInView({
    rootMargin,
    triggerOnce: true,
    skip: !isDeferred,
  });

  if (!isDeferred || inView) {
    return (
      <div ref={ref} className={className} data-lazy-hydrate={id}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={className}
      data-lazy-hydrate={id}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: "" }}
    />
  );
};
